// Cloudflare Turnstile for the signup / login / forgot-password forms. The site key is a build-time
// define (TURNSTILE_SITE_KEY); an empty key disables the widget and the backend accepts requests
// without a token when its secret is empty too. Cloudflare's test site keys work on any host:
// 1x00000000000000000000AA always passes (visible), 2x00000000000000000000AB always blocks,
// 3x00000000000000000000FF forces an interactive challenge.
const BUILD_SITE_KEY = typeof TURNSTILE_SITE_KEY !== 'undefined' ? TURNSTILE_SITE_KEY : ''
// Cypress runs the staging build on localhost, where the real site key never renders (Cloudflare only
// serves a widget on the hostnames registered for the key). Use the always-pass test key there: it
// yields the dummy token, which the backend accepts for the e2e accounts in E2E_TEST_MODE.
const CYPRESS_SITE_KEY = '1x00000000000000000000AA'
const SITE_KEY = BUILD_SITE_KEY && typeof window !== 'undefined' && window.Cypress ? CYPRESS_SITE_KEY : BUILD_SITE_KEY
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=popTurnstileLoaded'

let scriptPromise = null

export function isTurnstileEnabled () {
  return Boolean(SITE_KEY)
}

export function loadTurnstile () {
  if (!SITE_KEY) return Promise.resolve(null)
  if (window.turnstile && window.turnstile.render) return Promise.resolve(window.turnstile)
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      window.popTurnstileLoaded = () => resolve(window.turnstile)
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.defer = true
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('Failed to load Turnstile'))
      }
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

/**
 * Render the widget into `el`. `action` (signup, login, forgot_password) is echoed by the backend
 * check. Resolves the widget id, or null when Turnstile is disabled.
 */
export async function renderTurnstile (el, { action, onToken, onExpired, onError } = {}) {
  const turnstile = await loadTurnstile()
  if (!turnstile) return null
  return turnstile.render(el, {
    sitekey: SITE_KEY,
    action,
    theme: 'light',
    size: 'flexible',
    appearance: 'always',
    callback: (token) => onToken && onToken(token),
    'expired-callback': () => onExpired && onExpired(),
    'error-callback': (code) => {
      if (onError) onError(code)
      return true // handled: no console noise from the widget
    },
  })
}

/** Tokens are single-use: reset after every submit so the next one gets a fresh token. */
export function resetTurnstile (widgetId) {
  try {
    if (widgetId != null && window.turnstile) window.turnstile.reset(widgetId)
  } catch (e) {
    console.log(e)
  }
}

export function removeTurnstile (widgetId) {
  try {
    if (widgetId != null && window.turnstile) window.turnstile.remove(widgetId)
  } catch (e) {
    console.log(e)
  }
}
